import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import styles from './TeamDropdown.module.css';
import { fetchAPI } from '../utils/api';
import TeamLogo from './TeamLogo';

interface Team {
  team_key: string;
  team_name: string;
}

interface TeamDropdownProps {
  onSelect: (teamKey: string) => void;
  selectedTeamKey?: string;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
}

const TeamDropdown: React.FC<TeamDropdownProps> = ({ selectedTeamKey, onSelect, isOpen, onToggle }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const data = await fetchAPI('/teams');
        setTeams(data.teams);
        setIsLoading(false);
      } catch (err) {
        setError('Failed to load teams');
        setIsLoading(false);
      }
    };

    fetchTeams();
  }, []);

  const filteredTeams = searchTerm
    ? teams
        .filter(team =>
          team.team_name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => a.team_name.localeCompare(b.team_name))
    : teams.sort((a, b) => a.team_name.localeCompare(b.team_name));

  const selectedTeam = teams.find(team => team.team_key === selectedTeamKey);

  const handleDropdownToggle = (isOpen: boolean) => {
    onToggle(isOpen);
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredTeams.length > 0) {
      onSelect(filteredTeams[0].team_key);
      onToggle(false);
    }
  };

  return (
    <Dropdown 
      className={styles.teamDropdown} 
      onToggle={handleDropdownToggle}
      show={isOpen}
    >
      <Dropdown.Toggle variant="outline-secondary" id="dropdown-team" className={`${styles.noCaretToggle} ${styles.dropdownToggle}`}>
        {selectedTeam ? (
          <>
            <TeamLogo teamKey={selectedTeam.team_key} logoSize={16} />
            <span>{selectedTeam.team_name}</span>
          </>
        ) : selectedTeamKey === '' ? (
          'Any'
        ) : (
          'Teams'
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className={styles.dropdownMenu}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search teams..."
            className={styles.searchInput}
            ref={searchInputRef}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            value={searchTerm}
          />
        </div>
        <Dropdown.Divider />
        <Dropdown.Item
          onClick={() => onSelect('')}
          active={selectedTeamKey === ''}
          className={styles.dropdownItem}
        >
          Any
        </Dropdown.Item>
        <Dropdown.Divider />
        {isLoading && <Dropdown.Item>Loading...</Dropdown.Item>}
        {error && <Dropdown.Item className={styles.error}>{error}</Dropdown.Item>}
        {filteredTeams.map((team) => (
          <Dropdown.Item
            key={team.team_key}
            onClick={() => onSelect(team.team_key)}
            active={team.team_key === selectedTeamKey}
            className={styles.dropdownItem}
          >
            <TeamLogo teamKey={team.team_key} logoSize={16} />
            <span className={styles.teamName}>{team.team_name}</span>
          </Dropdown.Item>
        ))}
        {!isLoading && !error && filteredTeams.length === 0 && (
          <Dropdown.Item disabled>No teams found</Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default TeamDropdown;
